interface ErrorCause {
  type: string;
  dangerLevel: string;
}

interface UsageTrackerError extends Error {
  cause: ErrorCause;
}

export const createError = (name: string, msg: string, cause: ErrorCause) => {
  const error = new Error(msg) as UsageTrackerError;

  // Overrides the name/type of the Error
  error.name = name;

  // Overrides the StackTrace to be `ErrorType: Message`
  Object.defineProperty(error, 'stack', {
    value: `${name}: ${msg}`,
    writable: true,
    configurable: true,
  });

  // Adds Extra information to the Error object that can be used by the logger
  error.cause = cause;

  return error;
};

export const isUsageTrackerError = (error: any): error is UsageTrackerError => {
  if (error && error.name && error.stack && typeof error.cause === 'object') {
    const errorCause = error.cause as ErrorCause;

    return Boolean(errorCause && errorCause.type && errorCause.dangerLevel);
  }

  return false;
};

export const mergeErrorCauseWithSentry = (err: Error, options: any) => {
  if (!isUsageTrackerError(err)) {
    return options;
  }

  if (typeof options === 'object' && typeof options.extra === 'object') {
    return { ...options, extra: { ...options.extra, ...err.cause } };
  }

  return { ...options, extra: { ...err.cause } };
};

export const genericWarning = (message: string) =>
  createError('UsageTrackerGenericWarning', message, {
    type: 'generic warning',
    dangerLevel: 'This error does not break tracking or the execution thread.',
  });

export const genericError = (message: string) =>
  createError('UsageTrackerError', message, {
    type: 'generic error',
    dangerLevel: 'This error breaks the execution thread.',
  });

export const configError = (message: string) =>
  createError('UsageTrackerConfigError', message, {
    type: 'config error',
    dangerLevel: 'This error breaks the execution thread.',
  });

export const eventError = (message: string) =>
  createError('UsageTrackerEventError', message, {
    type: 'event error',
    dangerLevel: 'This error breaks tracking, but not the execution thread.',
  });
